import { ReactElement, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import { makeStyles } from '@material-ui/core/styles';
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormWatch,
} from 'react-hook-form';

import { AsyncFieldWrapper, Autocomplete } from 'components/standalone';

import { BumperOption, ContractorFormValues } from '../containers';

export interface BumperFieldProps {
  contractorId: string | undefined;
  control: Control<ContractorFormValues>;
  watch: UseFormWatch<ContractorFormValues>;
  getValues: UseFormGetValues<ContractorFormValues>;
  loadBumperOptions: (contractorId: string) => Promise<BumperOption[]>;
  bumperOptionsCache: BumperOption[];
  bumpersLoading?: boolean;
  bumpersError?: string | null;
  disabled?: boolean;
}

export const BumperField = ({
  contractorId,
  control,
  watch,
  getValues,
  loadBumperOptions,
  bumperOptionsCache,
  bumpersLoading = false,
  bumpersError = null,
  disabled = false,
}: BumperFieldProps): ReactElement | null => {
  const classes = useStyles();
  const history = useHistory();

  const bumperAvailable = watch('bumperAvailable');

  const handleLoadOptions = useCallback(
    async (): Promise<BumperOption[]> => {
      if (!contractorId) return [];
      return loadBumperOptions(contractorId);
    },
    [contractorId, loadBumperOptions],
  );

  const handleAddBumperClick = useCallback(() => {
    if (!contractorId) return;
    const formState = getValues();
    history.push({
      pathname: '/user/create',
      search: `contractorId=${contractorId}`,
      state: { formState, selectedBumpers: formState.selectedBumpers },
    });
  }, [contractorId, getValues, history]);

  if (!bumperAvailable) return null;

  return (
    <Box display="flex" alignItems="center" className={classes.root}>
      <Box className={classes.fieldWrapper}>
        <Controller
          name="selectedBumpers"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const bumperObjects = (field.value ?? [])
              .map(id => bumperOptionsCache.find(b => b.id === id))
              .filter((b): b is BumperOption => !!b);
            return (
              <AsyncFieldWrapper loading={bumpersLoading}>
                <Autocomplete<BumperOption>
                  disableCloseOnSelect
                  multiple
                  clientSideFilter
                  label="Bumpers"
                  loadOptions={handleLoadOptions}
                  loadTrigger="mount"
                  loading={bumpersLoading}
                  value={bumperObjects}
                  onChange={newValue => {
                    // Bumpers outside the Regional Admin's Regions (no loaded option) must survive every change.
                    const hidden = (field.value ?? []).filter(
                      (id: string) => !bumperObjects.some(b => b.id === id),
                    );
                    field.onChange([...hidden, ...newValue.map(b => b.id)]);
                  }}
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  error={error?.message ?? bumpersError ?? undefined}
                  data-cy="contractorFormBumpersAutocomplete"
                  fullWidth
                  disabled={disabled}
                />
              </AsyncFieldWrapper>
            );
          }}
        />
      </Box>
      <Box className={classes.addButtonWrapper}>
        <IconButton
          onClick={handleAddBumperClick}
          disabled={!contractorId || disabled}
          data-cy="contractorFormBumpersAddButton"
        >
          <AddIcon color="primary" />
        </IconButton>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  fieldWrapper: {
    width: '50%',
    margin: theme.spacing(0, 1.875),
  },
  addButtonWrapper: {
    width: '50%',
    marginRight: theme.spacing(3.75),
  },
}));

export default BumperField;
